import { AUDIO_CONFIG, ERROR_MESSAGES } from './constants';

export const createAudioEventHandlers = (
  audioState, 
  audioRef, 
  animationFrameRef, 
  updateFrame, 
  loadStreamingAudio, 
  onTimeUpdate,
  meetingDuration
) => {
  const handleLoadedMetadata = () => {
    // Don't override meetingDuration with audio.duration (which is just the chunk length)
    audioState.setIsLoading(false);

    // Apply current playback rate to the new audio element
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = audioState.playbackRate;
    }

    // Handle pending autoplay
    if (audioState.pendingPlay) {
      audioState.setPendingPlay(false);
      if (audio) {
        audio.play().catch(e => {
          console.warn('Autoplay prevented:', e);
          audioState.setError('Autoplay prevented - click play to start');
        });
      }
    }
  };

  const handleTimeUpdate = (() => {
    let lastUpdateTime = 0;
    
    return () => {
      if (audioRef.current) {
        const now = Date.now();
        const absoluteTime = audioRef.current.currentTime + audioState.streamStartOffset;
        audioState.setCurrentTime(absoluteTime);
        
        // Throttle parent notifications to reduce re-renders
        if (onTimeUpdate && (now - lastUpdateTime) >= AUDIO_CONFIG.THROTTLE_MS) {
          lastUpdateTime = now;
          onTimeUpdate(absoluteTime, audioState.isPlaying);
        }
        // Do not call drawTimeline here; the rAF loop handles drawing
      }
    };
  })();

  const handlePlay = () => {
    audioState.setIsPlaying(true);
    updateFrame();
  };

  const handlePause = () => {
    audioState.setIsPlaying(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    // Notify parent that we're no longer playing (to clear highlights)
    if (onTimeUpdate) {
      onTimeUpdate(audioState.currentTime, false);
    }
  };

  const handleError = () => {
    const err = audioRef.current?.error;
    audioState.setError(`Audio error${err?.code ? `: ${ERROR_MESSAGES[err.code]}` : ''}`);
    audioState.setIsLoading(false);
    audioState.setPendingPlay(false);
  };

  const handleProgress = () => {
    const audio = audioRef.current;
    if (audio?.buffered?.length > 0) {
      try {
        const bufferedEnd = audio.buffered.end(audio.buffered.length - 1);
        const actualStreamedDuration = audioState.streamStartOffset + bufferedEnd;
        audioState.setStreamedDuration(actualStreamedDuration);

        const totalDuration = audioState.duration || meetingDuration || 1;
        const progress = (actualStreamedDuration / totalDuration) * 100;
        audioState.setLoadProgress(Math.min(Math.max(progress, 0), 100));
      } catch (error) {
        console.warn('Progress calculation error:', error);
      }
    }
  };

  const handleEnded = () => {
    audioState.setIsPlaying(false);
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const stillTimeLeft = audioState.currentTime < (audioState.duration || 0) - 0.5;
    if (stillTimeLeft) {
      loadStreamingAudio(Math.floor(audioState.currentTime), true);
    }
  };

  return {
    handleLoadedMetadata,
    handleTimeUpdate,
    handlePlay,
    handlePause,
    handleError,
    handleProgress,
    handleEnded
  };
};
